import React, { FC, useMemo } from 'react';
import SectionMessage from '@atlaskit/section-message';

interface SubRefundWarningProps {
  slackChannel: string;
  approvalLineItems?: string[];
  standardLineItems?: string[];
  isExclusiveLineItemRequest?: boolean;
}

const List = ({ items, testId }: { items: string[]; testId?: string }) => {
  return (
    <ul data-testid={testId}>
      {items.map((item: string) => (
        <li key={item}>{item}</li>
      ))}
    </ul>
  );
};

export const WarningForSubRefundForAllLineItems: FC<SubRefundWarningProps> = ({
  slackChannel,
  isExclusiveLineItemRequest,
}) => {
  return (
    <SectionMessage title="Approval required" appearance="warning" testId="warning-sub-all-line-items">
      <p>
        This request is outside the standard limit - within the first paid month after the trial period for monthly
        subscriptions and within 30 days of payment for annual subscriptions
        {isExclusiveLineItemRequest && (
          <>
            {' '}
            <span data-testid="exclusive-line-items-text">for all line items you have selected</span>
          </>
        )}
        {'.'}
      </p>
      <p>
        Your approval request will be sent to the Slack channel <b>{slackChannel}</b> and Approval Desk. The refund
        initiation will be pending until the request is approved.
      </p>
    </SectionMessage>
  );
};

export const WarningForSubRefundForPartialLineItems: FC<SubRefundWarningProps> = ({
  slackChannel,
  approvalLineItems = [],
  standardLineItems = [],
}) => {
  return (
    <SectionMessage title="Approval required" appearance="warning" testId="warning-sub-partial-line-items">
      <p>
        This request is outside the standard limit - within the first paid month after the trial period for monthly
        subscriptions and within 30 days of payment for annual subscriptions
        {approvalLineItems.length > 0 ? ' - for these invoice line items:' : '.'}
        {approvalLineItems.length > 0 && <List items={approvalLineItems} testId="approval-line-items" />}
      </p>
      {standardLineItems.length > 0 && (
        <p>
          The request is within the standard limit for the other line items:
          <List items={standardLineItems} testId="standard-line-items" />
        </p>
      )}
      <p>
        The approval request will process all line items together, and will be sent to the Slack channel{' '}
        <b>{slackChannel}</b> and Approval Desk. The refund initiation for the full invoice will be pending until the
        request is approved.
      </p>
    </SectionMessage>
  );
};

export const ApprovalWarningForSubscriptionRefund: FC<SubRefundWarningProps> = ({
  slackChannel,
  approvalLineItems = [],
  standardLineItems = [],
  isExclusiveLineItemRequest = false,
}) => {
  const allLineItems = useMemo(() => standardLineItems.length <= 0, [standardLineItems.length]);
  if (allLineItems) {
    return (
      <WarningForSubRefundForAllLineItems
        slackChannel={slackChannel}
        isExclusiveLineItemRequest={isExclusiveLineItemRequest}
        approvalLineItems={approvalLineItems}
        standardLineItems={standardLineItems}
      />
    );
  }
  return (
    <WarningForSubRefundForPartialLineItems
      slackChannel={slackChannel}
      approvalLineItems={approvalLineItems}
      standardLineItems={standardLineItems}
    />
  );
};

export const ApprovalWarningForTaxRefund: FC<{ slackChannel: string }> = ({ slackChannel }) => (
  <SectionMessage title="Approval required" appearance="warning" testId="warning-tax-refund">
    This request is outside the standard limit - within 90 days of the invoice date. Your approval request will be sent
    to the Slack channel <b>{slackChannel}</b> and Approval Desk. The refund initiation will be pending until the
    request is approved.
  </SectionMessage>
);

export const ApprovalWarningForInvoiceDate: FC<{ slackChannel: string }> = ({ slackChannel }) => (
  <SectionMessage title="Approval required" appearance="warning" testId="warning-refund-invoice-date">
    This request is outside the standard limit - within 30 days of the invoice date. Your approval request will be sent
    to the Slack channel <b>{slackChannel}</b> and Approval Desk. The refund initiation will be pending until the
    request is approved.
  </SectionMessage>
);

export const ApprovalWarningForSubscriptionStartDate: FC<{ slackChannel: string }> = ({ slackChannel }) => (
  <SectionMessage title="Approval required" appearance="warning" testId="warning-refund-subscription-date">
    This request is outside the standard limit - within 30 days of the subscription start date. Your approval request
    will be sent to the Slack channel <b>{slackChannel}</b> and Approval Desk. The refund initiation will be pending
    until the request is approved.
  </SectionMessage>
);

export const ApprovalWarningForTrialExtension: FC<{ reason: string; slackChannel: string }> = ({
  reason,
  slackChannel,
}) => (
  <SectionMessage title="Approval required" appearance="warning" testId="warning-trial-extension">
    {!!reason && `${reason}. `}Your approval request will be sent to the Slack channel <b>{slackChannel}</b> and
    Approval Desk. The trial extension will be pending until the request is approved.
  </SectionMessage>
);

export const ApprovalWarningForOrderDiscount: FC<{ reason: string; slackChannel: string }> = ({
  reason,
  slackChannel,
}) => (
  <SectionMessage title="Approval required" appearance="warning" testId="warning-order-discount">
    {!!reason && `${reason}. `}When you submit your changes, your approval request will be sent to the Slack channel{' '}
    <b>{slackChannel}</b> and Approval Desk. Subscription changes will be pending until the discount is approved.
  </SectionMessage>
);

export const ApprovalWarningForProductDeletion: FC<{ reason: string; slackChannel: string }> = ({
  reason,
  slackChannel,
}) => (
  <SectionMessage title="Approval required" appearance="warning" testId="warning-product-delete-approval">
    {!!reason && `${reason}. `}Your approval request will be sent to the Slack channel <b>{slackChannel}</b> and
    Approval Desk. The product data deletion won’t be scheduled until the request is approved.
  </SectionMessage>
);

export const ApprovalWarningForQuoteDiscount: FC<{ reason: string; slackChannel: string }> = ({
  reason,
  slackChannel,
}) => (
  <SectionMessage title="Approval required" appearance="warning" testId="warning-quote-discount">
    {!!reason && `${reason}. `}Your approval request will be sent to the Slack channel <b>{slackChannel}</b> and
    Approval Desk. If approved, the quote will be automatically finalized and move from DRAFT to OPEN.
  </SectionMessage>
);

export const ApprovalWarningForQuoteExtension: FC<{ reason: string; slackChannel: string }> = ({ slackChannel }) => (
  <SectionMessage title="Approval required" appearance="warning" testId="warning-quote-extension">
    The allowed total number of 60 days past the initial expiry date has been exceeded. Your approval request will be
    sent to the Slack channel <b>{slackChannel}</b> and Approval Desk. After submitting this extension approval request,
    you can still revise the rest of the quote.
  </SectionMessage>
);

export const ApprovalWarningForPricingPlanChange: FC<{ reason: string | Array<string>; slackChannel: string }> = ({
  reason,
  slackChannel,
}) => (
  <SectionMessage title="Approval required" appearance="warning" testId="warning-pricing-plan-change">
    {!!reason && `${reason}. `}When you submit your changes, your approval request will be sent to the Slack channel{' '}
    <b>{slackChannel}</b> and Approval Desk. Subscription changes will be pending until the request is approved.
  </SectionMessage>
);

export const ApprovalWarningForPricingPlanAndDiscount: FC<{ reason: string[]; slackChannel: string }> = ({
  reason,
  slackChannel,
}) => (
  <SectionMessage title="Approval required" appearance="warning" testId="warning-pricing-plan-and-discount">
    {!!reason && Array.isArray(reason) && (
      <List items={reason} testId="approval-request-reasons-pricing-plan-discount" />
    )}
    When you submit your changes, your approval request will be sent to the Slack channel <b>{slackChannel}</b> and
    Approval Desk. Subscription changes will be pending until the request is approved.
  </SectionMessage>
);
